import React, { useCallback } from 'react';
import { FieldErrors, useForm, UseFormProps, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  ConfigureFieldInterface,
  DataTypeOfZodSchema,
  KeysOfZodSchema,
  ValidFormSubmitHandler,
  ZodSchema,
  ZodSchemaOrEffects,
} from '../types';

import { getRequired } from './getRequired';

// The useForm args, minus `resolver`, which we handle internally
// Provides schema-specific typesafety via our added parametric `schema` arg
export type UseKibbleFormArgs<Z extends ZodSchemaOrEffects> = Partial<
  Omit<UseFormProps<DataTypeOfZodSchema<Z>>, 'resolver'>
> & {
  schema: Z; // This `Z` param is what provides the typesafety specific to the passed schema
  onValidSubmit?: ValidFormSubmitHandler<Z>;
  onInvalidSubmit?: (errors: FieldErrors<DataTypeOfZodSchema<Z>>) => void;
  duringSubmit?: {
    /** Set to true to show a loading spinner during form submission */
    showSubmitButtonLoadingSpinner?: boolean;
    /** Set to true to disable the submit button during form submission */
    disableSubmitButton?: boolean;
  };
  isSubmitting?: boolean; // When using `duringSubmit` options, set this to true when you are doing async things with form submission.
  /** These props are not intended for public use, they are mostly conveniences for unit testing */
  UNSAFE_overrides?: {
    formCooldownMs?: number; // Override the default form cooldown period,
  };
};

// The useForm return, minus `register`, which we handle via our `configureField` method instead
// Also returns our added `formProps` and `submitButtonProps` objects
export type UseKibbleFormReturn<Z extends ZodSchemaOrEffects> = Omit<
  UseFormReturn<DataTypeOfZodSchema<Z>>,
  'register'
> & {
  configureField: (key: KeysOfZodSchema<Z>) => ConfigureFieldInterface<Z>;
  formProps: {
    onSubmit: React.FormEventHandler<HTMLFormElement>;
  };
  submitButtonProps: {
    type: 'submit';
    onClick: (e?: React.BaseSyntheticEvent) => unknown;
    disabled?: boolean;
    loading?: boolean;
  };
};

/** Amount of time to prevent submission of ANY useKibbleForm() after submit.
 * The only situation where we want to allow rapid-fire form submissions is during unit tests. */
const FORM_COOLDOWN_MS = process.env.JEST_WORKER_ID ? 0 : 1000;
/** Global boolean used to prevent duplicate form submissions.
 * We use this sync global because it is VERY fast, much faster than setting React state. */
let isFormInCooldown = false;

/** Our opinionated and slightly enhanced usage of `react-hook-form`'s `useForm` hook.
 * Provides schema-specific typesafety via our added parametric `schema` arg */
const useKibbleForm = <Z extends ZodSchemaOrEffects>(
  args: UseKibbleFormArgs<Z>
): UseKibbleFormReturn<Z> => {
  const {
    schema,
    onValidSubmit = () => {},
    onInvalidSubmit = () => {},
    isSubmitting: isSubmittingProp,
    duringSubmit = {},
    UNSAFE_overrides = {}, // eslint-disable-line camelcase
    ...rest
  } = args;

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  let zodObject: ZodSchema;
  // if the schema is a ZodEffect, pull out the underlying ZodObject
  // eslint-disable-next-line no-underscore-dangle
  if (schema._def.typeName === 'ZodEffects') {
    zodObject = schema._def.schema; // eslint-disable-line no-underscore-dangle
  } else {
    zodObject = schema as ZodSchema;
  }

  const { register, ...form } = useForm<DataTypeOfZodSchema<Z>>({
    ...rest,
    resolver: zodResolver(schema),
  });

  const handleValidSubmit = useCallback(
    (data: DataTypeOfZodSchema<Z>) => {
      if (isFormInCooldown) return; // prevent duplicate submissions in the cooldown period

      // start (and end) the cooldown period
      isFormInCooldown = true;
      setIsSubmitting(true);
      setTimeout(() => {
        isFormInCooldown = false;
        setIsSubmitting(false);
      }, UNSAFE_overrides.formCooldownMs || FORM_COOLDOWN_MS); // eslint-disable-line camelcase

      onValidSubmit(data, { setError: form.setError });
    },
    [onValidSubmit, form.setError, setIsSubmitting]
  );

  return {
    ...form,
    formProps: {
      onSubmit: form.handleSubmit(handleValidSubmit, onInvalidSubmit),
    },
    submitButtonProps: {
      type: 'submit',
      onClick: form.handleSubmit(handleValidSubmit, onInvalidSubmit),
      disabled: duringSubmit.disableSubmitButton ? isSubmittingProp || isSubmitting : undefined,
      loading: duringSubmit.showSubmitButtonLoadingSpinner
        ? isSubmittingProp || isSubmitting
        : undefined,
    },
    // use of ExtractSchemaKeys<Z> provides type safety for the key arg; it must be a key of the schema
    configureField: (key: KeysOfZodSchema<Z>) =>
      ({
        control: form.control,
        name: key,
        fieldProps: register(key),
        fieldError: form.getFieldState(key, form.formState).error,
        required: getRequired(zodObject, key),
      } as ConfigureFieldInterface<Z>), // return as the ConfigureFieldInterface for the specific schema
  };
};

export default useKibbleForm;
